import { useState, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useSelector, shallowEqual } from 'react-redux'
import { selectUserToken, selectUserUserId, selectCompanyId } from '../store/user'

import { driverClient } from '../clients/DriverClients'

import { CheckIn, UpdateCheckinData } from '../models/client/checkins'

type Return = {
    isLoading: boolean
    setRefresh: (data: boolean) => void
    setSiteId: (data: string) => void
    setPage: (pageIndex: number) => void
    siteId: string
    page: number
    pageSize: number
    totalPages: number
    totalItems: number
    searchTerm: string
    setSearchTerm: (data: string) => void
    checkins: CheckIn[]
    refetch: () => any
    updateCheckinData: (checkinId: string, params: UpdateCheckinData) => Promise<any>
}

const PAGE_SIZE = 50

export const useDriverCheckinHistory = (yardType: string): Return => {
    const [page, setPage] = useState(0)
    const [refresh, setRefresh] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const token = useSelector(selectUserToken, shallowEqual)
    const companyId = useSelector(selectCompanyId, shallowEqual)
    const [siteId, setSiteId] = useState('')
    const CHECKIN_LIST_QUERY_KEY = useMemo(
        () => ['getDriverCheckins', token, companyId, siteId, page, searchTerm],
        [token, companyId, siteId, page, searchTerm]
    )

    const {
        data,
        status: checkinListApiStatus,
        refetch,
    } = useQuery(
        CHECKIN_LIST_QUERY_KEY,
        () => {
            if (!token) return []
            return driverClient.searchCheckins({
                token,
                companyId,
                siteId,
                searchTerm,
                page,
                size: PAGE_SIZE,
            })
        },
        {
            enabled: true,
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            notifyOnChangeProps: ['data', 'error'],
        }
    )
    const { mutateAsync: updateCheckinData, status: updateDataStatus } = useMutation(
        driverClient.updateCheckin
    )

    const isLoading = checkinListApiStatus === 'loading'
    // @ts-ignore
    const total = data?.meta?.total || 0
    const totalPages = Math.ceil(total / PAGE_SIZE)
    return {
        isLoading,
        // @ts-ignore
        checkins: data?.data || [],
        setRefresh,
        siteId,
        setSiteId(siteId) {
            setSiteId(siteId)
        },
        page,
        setPage(pageIndex) {
            setPage(pageIndex)
        },
        pageSize: PAGE_SIZE,
        totalPages,
        totalItems: total,
        searchTerm,
        setSearchTerm(term) {
            setSearchTerm(term)
        },
        refetch,
        async updateCheckinData(checkinId, params) {
            if (!token) return []
            try {
                await updateCheckinData({
                    token,
                    checkinId,
                    params,
                })
                await refetch()
            } catch (e) {
                if (e instanceof Error) {
                    throw new Error('err')
                }
            }
        },
    }
}
